import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Tag, ArrowLeft, ChefHat, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getBlogPost, blogPosts } from '../data/blogPosts';

const renderContent = (block, i) => {
  switch (block.type) {
    case 'h2':
      return <h2 key={i} className="text-2xl font-bold text-charcoal mt-10 mb-4">{block.text}</h2>;
    case 'h3':
      return <h3 key={i} className="text-xl font-semibold text-charcoal mt-8 mb-3">{block.text}</h3>;
    case 'p':
      return <p key={i} className="text-charcoal/75 leading-relaxed mb-4">{block.text}</p>;
    case 'ul':
      return (
        <ul key={i} className="list-none space-y-2 mb-6">
          {block.items.map((item, j) => (
            <li key={j} className="flex items-start gap-3 text-charcoal/75">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-gold flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={i} className="space-y-2 mb-6 counter-reset-list">
          {block.items.map((item, j) => (
            <li key={j} className="flex items-start gap-3 text-charcoal/75">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gold/20 text-gold text-xs font-bold flex items-center justify-center mt-0.5">{j + 1}</span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      );
    default:
      return null;
  }
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getBlogPost(slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Header />
        <div className="text-center">
          <h1 className="text-4xl font-bold text-charcoal mb-4">Article introuvable</h1>
          <Link to="/blog" className="text-gold font-semibold hover:underline">Retour au blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const related = blogPosts.filter(p => p.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-cream">
      <Header />

      {/* Hero image */}
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
          onError={e => { e.target.parentNode.style.background = 'hsl(0,0%,12%)'; e.target.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-8 max-w-4xl mx-auto">
          <span className="inline-flex items-center gap-1 bg-gold text-charcoal text-xs font-bold px-3 py-1 rounded-full mb-4">
            <Tag className="w-3 h-3" />
            {post.category}
          </span>
          <h1 className="text-2xl sm:text-4xl font-bold text-cream leading-snug">{post.title}</h1>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Back + meta */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 pb-6 border-b border-gold/20">
          <Link to="/blog" className="inline-flex items-center gap-2 text-charcoal/50 hover:text-gold transition-colors text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
          <div className="flex items-center gap-4 text-sm text-charcoal/40">
            <span>{post.date}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {post.readTime} de lecture
            </span>
          </div>
        </div>

        {/* Excerpt */}
        <p className="text-xl text-charcoal/70 leading-relaxed mb-10 font-medium border-l-4 border-gold pl-5 italic">
          {post.excerpt}
        </p>

        {/* Content blocks */}
        <div className="prose-custom">
          {post.content.map((block, i) => renderContent(block, i))}
        </div>

        {/* CTA box */}
        <div className="mt-16 bg-charcoal rounded-2xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-gold/20 rounded-full mb-4">
            <ChefHat className="w-6 h-6 text-gold" />
          </div>
          <h3 className="text-2xl font-bold text-cream mb-3">Un événement à organiser ?</h3>
          <p className="text-cream/60 mb-6">Paëlla, méchoui, buffet… contactez-nous pour un devis gratuit dans le Var.</p>
          <Link
            to="/"
            onClick={() => setTimeout(() => {
              const el = document.getElementById('booking');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }, 300)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-charcoal font-bold rounded-lg hover:bg-gold/90 transition-all shadow"
          >
            Demander un devis gratuit
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-charcoal mb-8">Articles similaires</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {related.map(r => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="group flex gap-4 bg-cream rounded-xl p-4 hover:shadow-md transition-all border border-gold/10"
                >
                  <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-gold font-semibold">{r.category}</span>
                    <h3 className="text-sm font-bold text-charcoal mt-1 line-clamp-2 group-hover:text-burgundy transition-colors">{r.title}</h3>
                    <span className="text-xs text-charcoal/40 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {r.readTime}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;
